// Handle "deep links" to navigate to specific screens in the app.
// Deep links are URLs that open the app to a specific location.
// Example deep link URL: nvmsconnectlink://app/open?deeplink&orderguid=FF6F670D-A145-4A82-B01A-A08D5EB589F6
// Example web link URL:  http://localhost:4200?deeplink&orderguid=FF6F670D-A145-4A82-B01A-A08D5EB589F6
// (Order 4556413 )

// Link: http://localhost:4200?deeplink&orderguid=544A947A-27BB-4372-9894-986539C213F4
// (Order 4556427 for user NmvsMobileTest )

// Test draw example:
// Link: http://localhost:4200?deeplink&orderguid=074E1A98-06CC-40DC-984B-0129469FE157
// (Order 4646128)

// Data reset command example, run both of these.
// UPDATE [NVMSNet].[dbo].[OrderHeaders] SET OrderSTatus = 'Assigned' WHERE OrderID = 4556427
// UPDATE [NVMSNet].[dbo].[OrderDetails] SET VendorAccept = 0, VendorID=5 WHERE OrderID = 4556427

// To start a new user, especially for testing, use http://localhost:4200?logout

import { Injectable } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { ajaxCall, LoggingInterceptor } from './ajax-call.service';
import { CurrentUserService } from './current-user.service';
import { LoadingService } from './loading.service';

const RESTRICTED_JOB_ID_KEY = 'RestrictedOrderId';

@Injectable({ providedIn: 'root' })
export class DeepLinkService {
  private restrictedJobId: string | null = null; // job the user is locked to when deep link key is valid
  private restrictedJobUrlPath: string | null = null;

  constructor(
    private router: Router,
    private ajax: ajaxCall,
    private currentUserService: CurrentUserService,
    private loadingService: LoadingService
  ) {
    // Restore any previously stored restricted job id (for reloads)
    const storedJobId = sessionStorage.getItem(RESTRICTED_JOB_ID_KEY);
    if (storedJobId) {
      this.restrictedJobId = storedJobId;
    }

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        if (!this.restrictedJobId) {
          return;
        }

        const [path] = event.url.split('?');
        const allowedPath = `/public/jobs/${this.restrictedJobId}`;

        // Allow logout explicitly: clear header + restriction, send to login
        if (path === '/logout') {
          LoggingInterceptor.clear();
          this.clearRestriction();
          this.router.navigateByUrl('/login', { replaceUrl: true });
          return;
        }

        // Allow direct navigation to login so the user can switch accounts
        if (path === '/login') {
          return;
        }

        // Allow /public/jobs/:id and its sub-routes
        if (path === allowedPath || path.startsWith(`${allowedPath}/`)) {
          return;
        }
        if (path.startsWith('/public/deeplink-result')) {
          return;
        }

        // User is trying to leave the restricted job: force logout
        LoggingInterceptor.clear();
        this.clearRestriction();
        this.router.navigateByUrl('/login', { replaceUrl: true });
        return;
      }

      if (event instanceof NavigationEnd && this.restrictedJobUrlPath) {
        const destination = this.restrictedJobUrlPath;
        this.restrictedJobUrlPath = null;
        this.router.navigateByUrl(destination, { replaceUrl: true });
        this.loadingService.reset();
      }
    });

    // Fired when the app is opened from a link while running
    App.addListener('appUrlOpen', (event) => {
      this.handle(event.url);
    });

    // Fired when the app is launched cold from a deep link, or launched
    App.getLaunchUrl().then((result) => {
      if (result?.url) this.handle(result.url);
    });

    if (!Capacitor.isNativePlatform()) {
      const currentUrl = window.location.href;
      const url = new URL(currentUrl);
      if (
        url.searchParams.has('deeplink') &&
        url.searchParams.has('orderguid')
      ) {
        this.handle(currentUrl);
      }
    }
  }

  private handle(url: string) {
    const parsed = new URL(url);
    const params = parsed.searchParams;

    // Open the job via guid lookup.
    const orderGuid = params.get('orderguid');
    if (!orderGuid) {
      return;
    }

    // RULE: If a user is already logged in, do NOT allow orderguid deep links
    if (this.currentUserService.isLoggedIn) {
      alert('Job Deep Links not allowed for logged-in users.');
      return;
    }

    this.resolveOrderIdFromOrderGuid(orderGuid).then((orderId) => {
      if (!orderId) {
        // Invalid guid
        alert('This public link is invalid.');
        return;
      }

      // Lock the app to this job id (and persist to sessionStorage)
      this.restrictedJobId = orderId;
      sessionStorage.setItem(RESTRICTED_JOB_ID_KEY, orderId);

      // Server has updated the auth header (including RestrictedOrderId); refresh client-side user
      this.currentUserService.get().then(() => {
        this.ensureAssignedOrder(orderId).then((isAssigned) => {
          if (!isAssigned) {
            this.navigateToResult('unavailable');
            return;
          }
          this.navigateToJob(orderId);
        });
      });
    });
  }

  public hasRestrictedJob(): boolean {
    return !!this.restrictedJobId;
  }

  // Optional helper if you ever need the id
  public getRestrictedJobId(): string | null {
    return this.restrictedJobId;
  }

  private resolveOrderIdFromOrderGuid(
    orderGuid: string
  ): Promise<string | null> {
    return this.ajax
      .MakeCall<number>('/Account/GetOrderIdByGuid', { orderGuid: orderGuid })
      .then((orderId) => (orderId != null ? orderId.toString() : null))
      .catch((error): string | null => {
        console.error('Failed to resolve job guid', error);
        return null;
      });
  }

  private navigateToJob(jobId: string) {
    const path = `/public/jobs/${jobId}`;

    if (!this.router.navigated) {
      this.restrictedJobUrlPath = path;
      return;
    }

    this.router.navigateByUrl(path, { replaceUrl: true });
    this.loadingService.reset();
  }

  private navigateToResult(status: string) {
    const path = `/public/deeplink-result?status=${status}`;

    if (!this.router.navigated) {
      this.restrictedJobUrlPath = path;
      return;
    }

    this.router.navigateByUrl(path, { replaceUrl: true });
    this.loadingService.reset();
  }

  private ensureAssignedOrder(orderId: string): Promise<boolean> {
    return this.ajax
      .MakeCall<any>('/Order/Get', { id: Number(orderId) })
      .then((info) => {
        const status = String(
          info?.orderStatus ?? info?.OrderStatus ?? ''
        ).toLowerCase();
        return status === 'assigned';
      })
      .catch(() => false);
  }

  // Call this from your logout flow if you want to clear the restriction explicitly
  public clearRestriction() {
    this.restrictedJobId = null;
    sessionStorage.removeItem(RESTRICTED_JOB_ID_KEY);
  }
}
